import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useRecoilValue, waitForAll } from 'recoil';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import type { Image3D } from '~/domain/patient/image3d';
import {
    COMPLETE_PROGRESS,
    progressOf,
    type ImageStreamProgress,
} from '~/services/imageStreaming/imageStreamProgress';
import {
    imageStreamProgressFamily,
    incompleteSeriesUidsState,
} from '~/state/atoms/imageStreamingAtoms';
import { useSelectedPrimaryImage } from './useSelectedImages';

/**
 * Family key used when there is no image at all. A series UID is never empty,
 * so this cannot collide with a real one.
 */
const NO_SERIES_KEY = '';

/**
 * The completeness of one image, as React state.
 *
 * Prefers what the stream recorded; falls back to the live domain object when
 * nothing has been recorded. The fallback is what makes this correct for the
 * images that never stream — local-file opens and fully parsed series, which
 * report complete — and it also covers the frame between the viewer opening
 * and `useSliceStreaming` seeding the atom.
 *
 * @param image The volume to report on, or `undefined` for "no image".
 */
export function useImageStreamProgress(
    image: Image3D | undefined
): ImageStreamProgress {
    const recorded = useRecoilValue(
        imageStreamProgressFamily(image?.seriesUid ?? NO_SERIES_KEY)
    );

    // Read the live counters as scalars. `Image3D` mutates in place, so its
    // object identity never changes and cannot serve as a memo dependency;
    // the numbers can.
    const loadedSliceCount = image?.loadedSliceCount ?? 0;
    const expectedSliceCount = image?.expectedSliceCount ?? 0;
    const isComplete = image?.isComplete ?? true;
    const isIncompleteFinal = image?.isIncompleteFinal ?? false;
    const unavailableSliceCount = image?.unavailableSliceCount ?? 0;

    return useMemo(() => {
        if (recorded !== undefined) {
            return recorded;
        }
        if (image === undefined) {
            return COMPLETE_PROGRESS;
        }
        return {
            loadedSliceCount,
            expectedSliceCount,
            isComplete,
            isIncompleteFinal,
            unavailableSliceCount,
        };
    }, [
        recorded,
        image,
        loadedSliceCount,
        expectedSliceCount,
        isComplete,
        isIncompleteFinal,
        unavailableSliceCount,
    ]);
}

/**
 * An incomplete image's retrieval state, worded for display. Carries slice
 * counts only, never an identifier.
 */
export type ImageStreamStatus = {
    /** Slices are still arriving, or retrieval ended with slices missing. */
    kind: 'loading' | 'incomplete';
    /** The counts the words state. */
    progress: ImageStreamProgress;
    /** The state's one-word badge. */
    badge: string;
    /**
     * The count out of the total: the loaded count while loading, the
     * missing count once ended short.
     */
    counts: string;
    /** The badge and its counts, short enough for a chip or a title bar. */
    label: string;
    /** How much arrived, or how much is missing, out of the series' total. */
    value: string;
    /** The full explanation, including what stays unavailable meanwhile. */
    description: string;
};

/**
 * Words an image's progress for display, or `undefined` for a complete
 * image, which shows no status. An image that ended short reports what is
 * missing, never what arrived: it will not become complete.
 */
export function imageStreamStatusOf(
    progress: ImageStreamProgress,
    formatMessage: IntlShape['formatMessage']
): ImageStreamStatus | undefined {
    if (progress.isComplete) {
        return undefined;
    }
    const total = progress.expectedSliceCount;
    if (progress.isIncompleteFinal) {
        const unavailable = progress.unavailableSliceCount;
        const badge = formatMessage({ id: 'image.incomplete.badge' });
        return {
            kind: 'incomplete',
            progress,
            badge,
            counts: `${unavailable}/${total}`,
            label: `${badge} ${unavailable}/${total}`,
            value: formatMessage(
                { id: 'image.incomplete.value' },
                { unavailable, total }
            ),
            description: formatMessage(
                { id: 'image.incomplete.tooltip' },
                { unavailable, total }
            ),
        };
    }
    const loaded = progress.loadedSliceCount;
    const badge = formatMessage({ id: 'image.loading.badge' });
    return {
        kind: 'loading',
        progress,
        badge,
        counts: `${loaded}/${total}`,
        label: `${badge} ${loaded}/${total}`,
        value: formatMessage({ id: 'image.loading.value' }, { loaded, total }),
        description: formatMessage(
            { id: 'image.loading.tooltip' },
            { loaded, total }
        ),
    };
}

/**
 * The retrieval state of each image, worded for display and re-read as
 * slices arrive; `undefined` for an image that is complete, and for no
 * image. Resolves each image as {@link useImageStreamProgress} does: what
 * the stream recorded, else the live image.
 */
export function useImageStreamStatuses(
    images: ReadonlyArray<Image3D | undefined>
): ReadonlyArray<ImageStreamStatus | undefined> {
    const recorded = useRecoilValue(
        waitForAll(
            images.map((image) =>
                imageStreamProgressFamily(image?.seriesUid ?? NO_SERIES_KEY)
            )
        )
    );
    const intl = useIntl();
    return images.map((image, index) =>
        imageStreamStatusOf(
            recorded[index] ??
                (image === undefined ? COMPLETE_PROGRESS : progressOf(image)),
            intl.formatMessage
        )
    );
}

/** One image's retrieval state, as {@link useImageStreamStatuses} words it. */
export function useImageStreamStatus(
    image: Image3D | undefined
): ImageStreamStatus | undefined {
    return useImageStreamStatuses([image])[0];
}

/**
 * Whether an operation on the image(s) behind it must be refused, and why.
 *
 * `reason` is a localised, user-facing phrase suitable for a disabled-control
 * tooltip. It is `undefined` exactly when `isBlocked` is false.
 */
export type ImageCompletenessGate = {
    /** The operation must be refused. */
    isBlocked: boolean;
    /** Slices are still arriving. */
    isStreaming: boolean;
    /**
     * Retrieval finished short. Terminal, and NOT acknowledged away: an image
     * that will never be complete stays gated, because every gated operation
     * is a whole-volume one and a permanently missing slice makes every one of
     * them wrong in the same way it makes a streaming one wrong.
     */
    isIncompleteFinal: boolean;
    /** Localised reason, `undefined` when not blocked. */
    reason: string | undefined;
};

const NOT_BLOCKED: ImageCompletenessGate = {
    isBlocked: false,
    isStreaming: false,
    isIncompleteFinal: false,
    reason: undefined,
};

/**
 * Turns progress into a gate.
 *
 * The reasons are ordered the way the user would resolve them: an image that
 * ended short can never be fixed by waiting, so that reason wins over "still
 * loading" even though both are reachable from the same `!isComplete`.
 */
function toGate(
    progress: ImageStreamProgress,
    formatIncompleteFinal: (unavailable: number, total: number) => string,
    formatStreaming: (loaded: number, total: number) => string
): ImageCompletenessGate {
    if (progress.isComplete) {
        return NOT_BLOCKED;
    }
    if (progress.isIncompleteFinal) {
        return {
            isBlocked: true,
            isStreaming: false,
            isIncompleteFinal: true,
            reason: formatIncompleteFinal(
                progress.unavailableSliceCount,
                progress.expectedSliceCount
            ),
        };
    }
    return {
        isBlocked: true,
        isStreaming: true,
        isIncompleteFinal: false,
        reason: formatStreaming(
            progress.loadedSliceCount,
            progress.expectedSliceCount
        ),
    };
}

/**
 * Gate for whole-volume operations on one image.
 *
 * @param image The volume the operation would read, or `undefined`.
 */
export function useImageCompletenessGate(
    image: Image3D | undefined
): ImageCompletenessGate {
    const progress = useImageStreamProgress(image);
    const intl = useIntl();

    return useMemo(
        () =>
            toGate(
                progress,
                (unavailable, total) =>
                    intl.formatMessage(
                        { id: 'tool.disabled.imageIncomplete' },
                        { unavailable, total }
                    ),
                (loaded, total) =>
                    intl.formatMessage(
                        { id: 'tool.disabled.imageLoading' },
                        { loaded, total }
                    )
            ),
        [progress, intl]
    );
}

/**
 * Gate for operations on the image the viewer is showing: contouring, threshold
 * segmentation, reformat, export, and every backend task that ships the primary
 * series.
 */
export function usePrimaryImageCompletenessGate(): ImageCompletenessGate {
    return useImageCompletenessGate(useSelectedPrimaryImage());
}

/**
 * Gate for operations that read BOTH volumes — registration, local and backend
 * alike. Blocked while either one is incomplete; a rigid transform fitted
 * against a half-arrived moving image is wrong in exactly the way a half-
 * arrived fixed image is.
 *
 * The moving image is the one fused onto the primary in the primary row.
 *
 * The primary's reason wins when both are blocked: it is the image on screen,
 * so it is the one whose progress the user can see.
 */
export function useRegistrationImagesCompletenessGate(): ImageCompletenessGate {
    const primary = useImageCompletenessGate(useSelectedPrimaryImage());
    const secondary = useImageCompletenessGate(
        useActiveFusionTarget().secondaryImage
    );
    return primary.isBlocked ? primary : secondary;
}

/**
 * Gate for an arbitrary, user-chosen set of images — the export selection.
 *
 * The per-image hooks above need one Recoil subscription each, so they only
 * serve a fixed set known at render time. The export dialog's selection is
 * neither: the user can tick any image in the session, including ones that are
 * neither the primary nor the secondary. This reads the session-wide set of
 * incomplete series instead, one subscription regardless of how many images
 * are selected.
 *
 * The reason is deliberately count-free. With several images selected there is
 * no single "142 of 300" to quote, and inventing one for whichever image
 * happened to be first would be worse than saying plainly that the selection
 * contains an image that is not all there.
 *
 * The recorded set alone is not enough to decide with. It is written by the
 * stream, so an image is absent from it both when it is complete and when
 * nothing has reported on it yet — between the viewer opening and the stream
 * seeding it, and for any image no stream in this session ever touched. The
 * volume's own state is consulted alongside it, so an unrecorded incomplete
 * image blocks rather than reads as complete; a parsed series reports complete
 * and is unaffected. The per-image hooks resolve the same ambiguity the same
 * way.
 *
 * @param images The images the action would read. `undefined` entries are
 * ignored, so callers can pass optional values without filtering first.
 */
export function useImagesCompletenessGate(
    images: readonly (Image3D | undefined)[]
): ImageCompletenessGate {
    const incompleteSeriesUids = useRecoilValue(incompleteSeriesUidsState);
    const intl = useIntl();

    const blocked = images.some(
        (image) =>
            image !== undefined &&
            (incompleteSeriesUids.includes(image.seriesUid) ||
                !image.isComplete)
    );

    return useMemo(() => {
        if (!blocked) {
            return NOT_BLOCKED;
        }
        return {
            isBlocked: true,
            // Not resolved per image: the caller has a set, and one of them
            // being incomplete is the whole answer.
            isStreaming: false,
            isIncompleteFinal: false,
            reason: intl.formatMessage({
                id: 'tool.disabled.imageSelectionIncomplete',
            }),
        };
    }, [blocked, intl]);
}
